import { PluginSpec, ReactPart } from ".";
import { useEffect } from "react";
import * as React from "react";
import { useSelector } from "react-redux";
import { Editor, Path, PointRef, Transforms } from "slate";
import { useSlate } from "slate-react";

import { IAgent, selectAgents } from "store/entities/agents";

import { MentionElement } from "../types";

const MENTION_SYMBOL = "@";

function withMentions(
  onEnter: (pointRef: PointRef, accept: () => void) => void,
  onLeave: () => void,
  editor: Editor
): Editor {
  const { insertText, isInline, isVoid } = editor;
  const userOnLeave = onLeave;
  editor.isInline = (element): boolean => element.type === "mention" || isInline(element);
  editor.isVoid = (element): boolean => element.type === "mention" || isVoid(element);

  const state: { pointRef: PointRef | null; mentionedAgents: Set<string> } = {
    pointRef: null,
    mentionedAgents: new Set<string>(),
  };

  editor.mentions = {
    onEnter,
    onLeave(): void {
      state.pointRef?.unref();
      state.pointRef = null;
      userOnLeave();
    },
    mentionedAgents(): string[] {
      return Array.from(state.mentionedAgents);
    },
    isActive(): boolean {
      return Boolean(state.pointRef && state.pointRef.current !== null);
    },
  };

  editor.removeMention = (agentID: string): void => {
    state.mentionedAgents.delete(agentID);
  };

  editor.registerMention = (agent: IAgent): void => {
    state.mentionedAgents.add(agent.id);
  };

  editor.insertText = (text: string): void => {
    insertText(text);

    if (text === MENTION_SYMBOL) {
      const point = editor.selection
        ? Editor.point(editor, editor.selection, { edge: "start" })
        : Editor.start(editor, []);

      if (point) {
        const beforeSymbolPoint = Editor.before(editor, point, { unit: "character" });
        if (!beforeSymbolPoint) return;
        const beforeCharacterPoint = Editor.before(editor, beforeSymbolPoint, { unit: "character" });
        const afterCharacterPoint = Editor.after(editor, point, { unit: "character" });
        const beforeCharacter = beforeCharacterPoint
          ? Editor.string(editor, Editor.range(editor, beforeCharacterPoint, beforeSymbolPoint))
          : "";

        const afterCharacter = afterCharacterPoint
          ? Editor.string(editor, Editor.range(editor, point, afterCharacterPoint))
          : "";

        if (beforeCharacter.trim().length === 0 && afterCharacter.trim().length === 0) {
          const beforePoint = Editor.before(editor, point, { unit: "character" });
          if (!beforePoint) return;
          const pointRef = Editor.pointRef(editor, beforePoint);
          editor.mentions.onEnter(pointRef, () => {
            state.pointRef = pointRef;
          });
        }
      }
    }
  };

  editor.insertMention = (agent: IAgent): void => {
    const { pointRef } = state;

    if (editor.mentions.isActive() && pointRef && pointRef.current) {
      const symbolRange = Editor.range(
        editor,
        pointRef.current,
        Editor.after(editor, pointRef.current, { unit: "character" })
      );

      Transforms.insertNodes(
        editor,
        [{ type: "mention", agentID: agent.id, agentName: agent.name, children: [{ text: "" }] }],
        { at: symbolRange, voids: true }
      );

      try {
        const [, path] = Editor.node(editor, Path.next(pointRef.current.path));
        const afterPoint = Editor.after(editor, Editor.end(editor, path));

        if (!afterPoint) throw new Error("No after point");

        Transforms.select(editor, afterPoint);
      } catch {
        setTimeout(() => Transforms.select(editor, Editor.start(editor, [0, 2])));
      }

      editor.mentions.onLeave();
    }
  };

  return editor;
}

const EditorMention: React.FC<MentionElement> = ({ agentID, agentName }) => {
  const editor = useSlate();
  const agents = useSelector(selectAgents);

  useEffect(() => {
    if (agents[agentID]) {
      editor.registerMention(agents[agentID]);
    }

    return (): void => {
      editor.removeMention(agentID);
    };
  }, [agentID, agents, editor]);

  return <span className="mention">@{agentName}</span>;
};

export default function buildMentionsPlugin(
  onEnter: (pointRef: PointRef, accept: () => void) => void,
  onLeave: () => void
): PluginSpec {
  const configuredPlugin = (editor: Editor): Editor => withMentions(onEnter, onLeave, editor);
  const reactPart: ReactPart = {
    renderElement(props, _editor, next) {
      if (props.element.type === "mention") {
        return (
          <span {...props.attributes} data-testid={`editor-mention-${props.element.agentID}`}>
            <EditorMention {...(props.element as MentionElement)} />
            {props.children}
          </span>
        );
      }

      return next(props);
    },
  };

  return [configuredPlugin, reactPart];
}
